<template>
  <div
    v-if="slides.length > 0"
    class="position-relative mt-0 mt-md-5 mt-lg-0 carousell-images yes-carousel ms-lg-4"
  >
    <div class="position-relative pt-0 pt-md-2 pt-lg-0">
      <swiper
        :modules="modules"
        :style="{
          '--swiper-navigation-color': '#151515',
          '--swiper-pagination-color': '#555865',
          '--swiper-pagination-bullet-size': '10px',
          '--swiper-pagination-bullet-width': widthBullet + '%',
          '--swiper-pagination-bullet-active-width': widthActiveBullet + '%',
          '--swiper-pagination-bullet-height': '6px',
          '--swiper-pagination-bullet-inactive-color': '#555865',
          '--swiper-pagination-bullet-inactive-opacity': '0.2',
          '--swiper-pagination-bullet-opacity': '1',
          '--swiper-pagination-bullet-horizontal-gap': '2px',
          '--swiper-pagination-bullet-vertical-gap': '2px',
          '--swiper-pagination-bullet-border-radius': '0px',
        }"
        :lazy="true"
        :slides-per-view="1"
        :loop="true"
        :space-between="0"
        :navigation="false"
        :pagination="pagination"
        :autoplay="{
          delay: 6500,
          disableOnInteraction: false,
        }"
        class="CustomSwiper"
        effect="fade"
        @swiper="onSwiper"
      >
        <!-- <swiper-slide v-if="loading">
          <div class="spinner-border text-primary py-5">
            <span class="visually-hidden">Loading...</span>
          </div>
        </swiper-slide> -->
        <swiper-slide v-for="(slide, index) in slides" :key="index">
          <base-link :to="slide.action_url || '#'">
            <img
              v-if="slide.image && !imageErrors[slide.id]"
              v-lazy="getImage(slide)"
              width="1200"
              height="450"
              :alt="slide.name"
              style=""
              @error="imageErrors[slide.id] = true"
            />
            <div
              v-if="imageErrors[slide.id]"
              class="d-flex justify-content-center align-items-center"
            >
              <span
                class="text-secondary fw-semibold text-capitalize fs-8 px-2 text-center"
              >
                {{ slide.name }}
              </span>
            </div>
          </base-link>
        </swiper-slide>
      </swiper>
      <div
        v-if="slides.length > 1"
        class="position-absolute top-0 end-0 d-none d-lg-block"
        :style="{ zIndex: 1 }"
      >
        <button
          v-if="runningAutoplay"
          type="button"
          class="btn btn-link m-4 p-2 rounded-pill text-primary"
          :style="{ backgroundColor: 'rgba(179, 179, 179, .5)' }"
          @click="swiperInstance.autoplay.stop()"
        >
          <i class="bi bi-pause-fill fs-1" />
        </button>
        <button
          v-else
          type="button"
          class="btn btn-link m-4 p-2 rounded-pill text-primary"
          :style="{ backgroundColor: 'rgba(179, 179, 179, .5)' }"
          @click="swiperInstance.autoplay.start()"
        >
          <i class="bi bi-play-fill fs-1" />
        </button>
      </div>
      <div
        v-if="slides.length > 1"
        class="position-absolute top-50 end-0 translate-middle-y d-none d-lg-block"
        :style="{ zIndex: 1 }"
        @click="swiperInstance.slideNext()"
      >
        <div class="swiper-button-next-carousel m-3 text-primary">
          <i
            class="bi bi-chevron-compact-right"
            :style="{ fontSize: '50px !important' }"
          />
        </div>
      </div>
      <div
        v-if="slides.length > 1"
        class="position-absolute top-50 start-0 translate-middle-y pointer d-none d-lg-block"
        :style="{ zIndex: 1 }"
        @click="swiperInstance.slidePrev()"
      >
        <div class="swiper-button-prev-carousel m-3 text-primary">
          <i
            class="bi bi-chevron-compact-left"
            :style="{ fontSize: '50px !important' }"
          />
        </div>
      </div>
    </div>
    <swiper
      v-if="secondarySlides.length > 0"
      :modules="modules"
      :watch-slides-progress="true"
      :loop="true"
      :space-between="15"
      :slides-per-view="3"
      :free-mode="true"
      class="CustomSwiperThumb d-none d-lg-block"
    >
      <swiper-slide
        v-for="(slide, index) in secondarySlides"
        :key="index"
        class="opacity-100"
      >
        <base-link :to="slide.action_url || '#'" :external="true" class="w-100">
          <img
            v-if="slide.image && !imageErrors[slide.id]"
            v-lazy="getImage(slide)"
            width="370"
            height="150"
            :alt="slide.name"
            class="small-slider-img"
            @error="imageErrors[slide.id] = true"
          />
          <div
            v-if="imageErrors[slide.id]"
            class="bg-body-secondary d-flex justify-content-center align-items-center w-100 CustomSwiperThumbImage"
          >
            <span
              class="text-secondary fw-semibold text-capitalize fs-8 px-2 text-center"
            >
              {{ slide.name }}
            </span>
          </div>
        </base-link>
      </swiper-slide>
    </swiper>
  </div>
  <div v-else class="no-carousel ms-lg-3"></div>
</template>
<script lang="ts">
import { ref } from "vue";
// Import Swiper Vue.js components
import { Swiper, SwiperSlide } from "swiper/vue";

// import required modules
import {
  FreeMode,
  Thumbs,
  EffectFade,
  Pagination,
  Autoplay,
  Navigation,
} from "swiper/modules";
import BaseLink from "~/components/buttons/BaseLink.vue";
import { getFinalEshopUrl } from "@/utils/general";
import "swiper/swiper-bundle.css";
import type { Swiper as SwiperType } from "swiper/types";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";

interface Slide {
  id: number;
  name: string;
  image: string;
  action_url: string;
  sm_image_url: string;
}

export default {
  components: {
    Swiper,
    SwiperSlide,
    BaseLink,
  },
  props: {
    slides: {
      type: Array<Slide>,
      default() {
        return [];
      },
    },
    secondarySlides: {
      type: Array<Slide>,
      default() {
        return [];
      },
    },
    loading: {
      type: Boolean,
    },
  },
  setup() {
    const swiperInstance = ref();

    return {
      swiperInstance,
      modules: [FreeMode, Thumbs, EffectFade, Pagination, Autoplay, Navigation],
      pagination: {
        clickable: true,
        renderBullet: function (_index: number, className: string) {
          return (
            '<span class="' +
            className +
            ' d-lg-none" style="transition: all 0.3s ease-out !important;"></span>'
          );
        },
      },
    };
  },
  data: function () {
    return {
      imageErrors: [] as boolean[],
      runningAutoplay: true,
      isMobileSize: useBreakpoints(breakpointsBootstrapV5).isSmallerOrEqual(
        "md",
      ),
    };
  },
  computed: {
    widthBullet() {
      return Math.floor(100 / (this.slides.length + 1));
    },
    widthActiveBullet() {
      return 90 - this.widthBullet * (this.slides.length - 1);
    },
  },
  methods: {
    getFinalUrl(path: string) {
      return getFinalEshopUrl(path);
    },
    getImage(slider: Slide) {
      if (this.isMobileSize && slider.sm_image_url) {
        return slider.sm_image_url;
      } else {
        return slider.image;
      }
    },
    onSwiper(swiper: SwiperType) {
      this.swiperInstance = swiper;
      const stopAutoplay = (() => {
        this.runningAutoplay = false;
      }).bind(this);
      const autoplay = (() => {
        this.runningAutoplay = true;
      }).bind(this);
      this.swiperInstance.on("autoplayStop", stopAutoplay);
      this.swiperInstance.on("autoplayStart", autoplay);
    },
  },
};
</script>
<style lang="sass" scoped>
.swiper
    width: 100%
    height: 100%

.swiper-slide
    text-align: center
    font-size: 18px
    background: #fff
    display: flex
    justify-content: center
    align-items: center

.CustomSwiper .swiper-slide
    display: block
    margin: 0 auto
    width: 390px
    height: auto
    min-height: 216px
    max-height: 420px

    @media (min-width: 768px)
        min-height: 450px
        max-height: 580px

.CustomSwiper
.CustomSwiper .swiper-slide img
    width:100%
    height: 100%


.CustomSwiperThumb
    box-sizing: border-box
    padding: 0 10px
    position: absolute
    left: .5%
    width: 99%
    background-color: transparent
    height: 100px
    bottom: -35px

    @media (min-width: 768px)
        bottom: -115px
        height: 170px

    @media (min-width: 992px)
        bottom: -120px

    @media (min-width: 1200px)
        bottom: -105px

    @media (min-width: 1400px)
        bottom: -105px

    @media (min-width: 1700px)
        bottom: -40px

    @media (min-width: 1800px)
        bottom: -70px

.CustomSwiperThumb .swiper-slide
    width: 25%
    height: 100%
    opacity: 0.7

.CustomSwiperThumb .swiper-slide-thumb-active
    opacity: 1

.CustomSwiperThumbImage
    display: block
    width: 100%
    object-fit: fill
    height: 80px
    @media (min-width: 992px)
        height: 15px

.swiper-pagination-bullet-active
    width: var(--swiper-pagination-bullet-active-width, var(--swiper-pagination-bullet-size, 8px)) !important

.swiper-button-next-carousel, .carousel .swiper-button-prev-carousel
    background-color: transparent
    padding: 30px 5px !important
    border-radius: 15px !important
    cursor: pointer
</style>
